import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Award, Plus, Edit2, Trash2, AlertCircle, CheckCircle2, Star } from 'lucide-react';

interface RuleForm {
  name: string;
  starNumber: number;
  displayOrder: number;
  directBusinessRequired: number;
  teamBusinessRequired: number;
  requiresTeamBusiness: boolean;
  rewardOptionA: string;
  rewardOptionB: string;
  rewardOptionBEnabled: boolean;
  rewardDescription: string;
  isActive: boolean;
}

const INITIAL_FORM: RuleForm = {
  name: '', starNumber: 1, displayOrder: 1,
  directBusinessRequired: 0, teamBusinessRequired: 0, requiresTeamBusiness: false,
  rewardOptionA: '', rewardOptionB: '', rewardOptionBEnabled: true,
  rewardDescription: '', isActive: true,
};

function RuleFormModal({ isOpen, onClose, rule }: { isOpen: boolean; onClose: () => void; rule?: any }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<RuleForm>(INITIAL_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const isEdit = !!rule;

  useEffect(() => {
    if (isOpen) {
      if (rule) {
        setForm({
          name: rule.name || '',
          starNumber: rule.starNumber || 1,
          displayOrder: rule.displayOrder || 0,
          directBusinessRequired: parseFloat(rule.directBusinessRequired) || 0,
          teamBusinessRequired: parseFloat(rule.teamBusinessRequired) || 0,
          requiresTeamBusiness: rule.requiresTeamBusiness || false,
          rewardOptionA: rule.rewardOptionA || '',
          rewardOptionB: rule.rewardOptionB || '',
          rewardOptionBEnabled: rule.rewardOptionBEnabled !== false,
          rewardDescription: rule.rewardDescription || '',
          isActive: rule.isActive !== false,
        });
      } else {
        setForm(INITIAL_FORM);
      }
      setError('');
      setSuccess(false);
    }
  }, [isOpen, rule]);

  const mutation = useMutation({
    mutationFn: (data: RuleForm) => isEdit ? api.patch(`/admin/awards/rules/${rule.id}`, data) : api.post('/admin/awards/rules', data),
    onSuccess: () => { setSuccess(true); qc.invalidateQueries({ queryKey: ['award-rules'] }); },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to save rule'),
  });

  const set = (key: keyof RuleForm, value: any) => setForm(prev => ({ ...prev, [key]: value }));
  const handleSubmit = () => {
    setError('');
    if (!form.name.trim()) { setError('Star name is required'); return; }
    if (!form.rewardOptionA.trim()) { setError('Reward Option A is required'); return; }
    if (form.directBusinessRequired < 0) { setError('Direct business must be >= 0'); return; }
    if (form.requiresTeamBusiness && form.teamBusinessRequired <= 0) { setError('Team business required when enabled'); return; }
    mutation.mutate(form);
  };
  const handleClose = () => { onClose(); };
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEdit ? 'Edit Award Rule' : 'Create Award Rule'} maxWidth="max-w-2xl">
      {success ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-400">{isEdit ? 'Rule Updated' : 'Rule Created'}</p>
              <p className="text-xs text-slate-400 mt-0.5">Award rule saved successfully</p>
            </div>
          </div>
          <Button onClick={handleClose} className="w-full">Done</Button>
        </div>
      ) : (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Star Name</label>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Star 1" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Star Number</label>
              <input type="number" value={form.starNumber} onChange={e => set('starNumber', parseInt(e.target.value) || 1)} className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm focus:outline-none focus:border-primary-500/50 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Display Order</label>
              <input type="number" value={form.displayOrder} onChange={e => set('displayOrder', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm focus:outline-none focus:border-primary-500/50 transition-all" />
            </div>
          </div>

          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-4">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Business Requirements</p>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Direct Business Required</label>
              <input type="number" value={form.directBusinessRequired || ''} onChange={e => set('directBusinessRequired', parseFloat(e.target.value) || 0)} placeholder="200000" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-slate-400">Require Team Business</label>
              <button type="button" onClick={() => set('requiresTeamBusiness', !form.requiresTeamBusiness)} className={`relative w-10 h-5 rounded-full transition-colors ${form.requiresTeamBusiness ? 'bg-primary-600' : 'bg-white/10'}`}>
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.requiresTeamBusiness ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            {form.requiresTeamBusiness && (
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Team Business Required</label>
                <input type="number" value={form.teamBusinessRequired || ''} onChange={e => set('teamBusinessRequired', parseFloat(e.target.value) || 0)} placeholder="600000" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
              </div>
            )}
          </div>

          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-4">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Reward Options</p>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Reward Option A *</label>
              <input type="text" value={form.rewardOptionA} onChange={e => set('rewardOptionA', e.target.value)} placeholder="e.g. Goa Trip" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-slate-400">Enable Reward Option B</label>
              <button type="button" onClick={() => set('rewardOptionBEnabled', !form.rewardOptionBEnabled)} className={`relative w-10 h-5 rounded-full transition-colors ${form.rewardOptionBEnabled ? 'bg-primary-600' : 'bg-white/10'}`}>
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.rewardOptionBEnabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            {form.rewardOptionBEnabled && (
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Reward Option B</label>
                <input type="text" value={form.rewardOptionB} onChange={e => set('rewardOptionB', e.target.value)} placeholder="e.g. Tablet" className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all" />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Reward Description</label>
              <textarea value={form.rewardDescription} onChange={e => set('rewardDescription', e.target.value)} rows={2} className="w-full px-3 py-2 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-primary-500/50 transition-all resize-none" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-slate-400">Status</label>
            <button type="button" onClick={() => set('isActive', !form.isActive)} className={`relative w-10 h-5 rounded-full transition-colors ${form.isActive ? 'bg-primary-600' : 'bg-white/10'}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-5' : ''}`} />
            </button>
            <span className="text-xs text-slate-400">{form.isActive ? 'Active' : 'Inactive'}</span>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} isLoading={mutation.isPending} className="flex-1">{isEdit ? 'Update Rule' : 'Create Rule'}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function AdminAwardsPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

  const { data: rules, isLoading } = useQuery({
    queryKey: ['award-rules'],
    queryFn: () => api.get('/admin/awards/rules').then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/awards/rules/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['award-rules'] }); setDeleteConfirm(null); },
  });

  const toggleActive = useMutation({
    mutationFn: (data: { id: string; isActive: boolean }) => api.patch(`/admin/awards/rules/${data.id}`, { isActive: data.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['award-rules'] }),
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Awards & Rewards</h1>
          <p className="text-slate-400 mt-1">Configure star achievements and reward options</p>
        </div>
        <Button onClick={() => { setEditingRule(null); setModalOpen(true); }}><Plus className="w-4 h-4" />Add Star</Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-64 rounded-xl bg-white/[0.02] animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rules?.map((rule: any) => (
            <div key={rule.id} className={`d-card-hover p-5 space-y-4 ${!rule.isActive ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Star className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{rule.name}</h3>
                    <p className="text-xs text-slate-500">Star #{rule.starNumber}</p>
                  </div>
                </div>
                <Badge status={rule.isActive ? 'ACTIVE' : 'INACTIVE'} />
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Direct Business</span>
                  <span className="font-semibold text-white">{formatCurrency(rule.directBusinessRequired)}</span>
                </div>
                {rule.requiresTeamBusiness && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Team Business</span>
                    <span className="font-semibold text-white">{formatCurrency(rule.teamBusinessRequired)}</span>
                  </div>
                )}
                {!rule.requiresTeamBusiness && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Team Business</span>
                    <span className="text-slate-400 text-xs">Not Required</span>
                  </div>
                )}
              </div>

              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] space-y-1.5">
                <p className="text-xs text-slate-500 font-medium">Rewards</p>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full">A</span>
                  <span className="text-sm text-white">{rule.rewardOptionA}</span>
                </div>
                {rule.rewardOptionBEnabled && rule.rewardOptionB && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-primary-500/10 text-primary-400 px-2 py-0.5 rounded-full">B</span>
                    <span className="text-sm text-white">{rule.rewardOptionB}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2 border-t border-white/[0.06]">
                <button onClick={() => { setEditingRule(rule); setModalOpen(true); }} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.04] text-slate-400 text-xs hover:text-white hover:bg-white/[0.08] transition-colors">
                  <Edit2 className="w-3.5 h-3.5" />Edit
                </button>
                <button onClick={() => toggleActive.mutate({ id: rule.id, isActive: !rule.isActive })} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.04] text-slate-400 text-xs hover:text-white hover:bg-white/[0.08] transition-colors">
                  {rule.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => setDeleteConfirm(rule)} className="flex items-center justify-center px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rules?.length === 0 && (
        <div className="text-center py-12">
          <Award className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No award rules configured yet</p>
          <Button onClick={() => { setEditingRule(null); setModalOpen(true); }} className="mt-4">Create First Rule</Button>
        </div>
      )}

      <RuleFormModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingRule(null); }} rule={editingRule} />

      {deleteConfirm && (
        <Modal isOpen={true} onClose={() => setDeleteConfirm(null)} title="Delete Rule">
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Are you sure you want to delete <span className="text-white font-medium">{deleteConfirm.name}</span>? This cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)} className="flex-1">Cancel</Button>
              <Button variant="danger" onClick={() => deleteMutation.mutate(deleteConfirm.id)} isLoading={deleteMutation.isPending} className="flex-1">Delete</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
